import React, { useState, useEffect } from 'react';
import { Share2, Users, ChevronRight, X } from 'lucide-react';
import { getConnections } from '../services/connectionService';
import { getSharedGroups, getSharedCards } from '../services/sharingService';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-hot-toast';
import { unshareGroup, unshareCard } from '../services/sharingService';

const ConnectionsList = ({ currentUser, cards = [], groups = [], onShare }) => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sharedGroups, setSharedGroups] = useState({});
  const [sharedCards, setSharedCards] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedCards, setExpandedCards] = useState({});

  // Add defensive checks
  const safeGroups = (groups?.filter(group => group != null) || []).map(group =>
    typeof group === 'string' ? { id: group, name: group } : group
  );
  const safeCards = cards?.filter(card => card != null) || [];

  // Toggle handlers
  const toggleGroups = (userId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const toggleCards = (userId) => {
    setExpandedCards(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Unshare handlers
  const handleUnshareGroup = async (group, recipientId) => {
    try {
      await unshareGroup(currentUser.uid, recipientId, group.groupName);
      
      // Update local state
      setSharedGroups(prev => ({
        ...prev,
        [recipientId]: (prev[recipientId] || []).filter(g => g.groupName !== group.groupName)
      }));
      
      toast.success(`Unshared group "${group.groupName}"`);
    } catch (error) {
      console.error('Error unsharing group:', error);
      setError(error.message);
    }
  };

  const handleUnshareCard = async (card, recipientId) => {
    try {
      await unshareCard(currentUser.uid, recipientId, card.id);
      
      // Update the local state to reflect the change
      setSharedCards(prev => ({
        ...prev,
        [recipientId]: prev[recipientId].filter(c => c.id !== card.id)
      }));
      
      toast.success('Card unshared successfully');
    } catch (error) {
      console.error('Error unsharing card:', error);
      toast.error(error.message || 'Failed to unshare card');
    }
  };

  const loadConnectionsAndSharedItems = async () => {
    if (!currentUser?.uid) {
      console.log('No current user, skipping connections load');
      return;
    }

    try {
      console.log('Loading connections for user:', currentUser.uid);
      const userConnections = await getConnections(currentUser.uid);
      console.log('Raw connections:', userConnections);
      
      if (!Array.isArray(userConnections)) {
        console.error('Unexpected connections format:', userConnections);
        setError('Invalid connections data format');
        return;
      }

      // Filter out invalid connections first
      const validConnections = userConnections.filter(conn => 
        conn?.users?.length === 2 && 
        conn.users.every(id => id !== null) &&
        conn.status === 'active'
      );
      
      console.log('Valid connections:', validConnections);

      // Enrich connections with user data
      const enrichedConnections = await Promise.all(
        validConnections.map(async (connection) => {
          const otherUserId = connection.users.find(id => id !== currentUser.uid);
          if (!otherUserId) {
            console.log('No other user found in connection:', connection);
            return null;
          }

          try {
            const userDocRef = doc(db, 'users', otherUserId);
            const userDoc = await getDoc(userDocRef);
            
            if (!userDoc.exists()) {
              console.log('User document not found:', otherUserId);
              return {
                ...connection,
                otherUser: {
                  id: otherUserId,
                  email: 'User not found',
                  displayName: 'Unknown User'
                }
              };
            }
            
            const userData = userDoc.data();
            return {
              ...connection,
              otherUser: {
                id: otherUserId,
                email: userData.email || 'No email',
                displayName: userData.displayName || userData.email || 'Unknown User'
              }
            };
          } catch (error) {
            console.error('Error fetching user data:', error);
            return null;
          }
        })
      );

      const finalConnections = enrichedConnections.filter(conn => conn !== null);
      console.log('Final enriched connections:', finalConnections);
      
      // Load shared items for each connection
      const sharedGroupsMap = {};
      const sharedCardsMap = {};
      
      await Promise.all(
        finalConnections.map(async (connection) => {
          if (connection.otherUser?.id) {
            const [groups, cards] = await Promise.all([
              getSharedGroups(currentUser.uid, connection.otherUser.id),
              getSharedCards(connection.otherUser.id, currentUser.uid)
            ]);
            sharedGroupsMap[connection.otherUser.id] = groups;
            sharedCardsMap[connection.otherUser.id] = cards;
          }
        })
      );
      
      setSharedGroups(sharedGroupsMap);
      setSharedCards(sharedCardsMap);
      setConnections(finalConnections);
    } catch (err) {
      console.error('Error loading connections:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnshare = async (toUserId, groupName) => {
    try {
      await unshareGroup(currentUser.uid, toUserId, groupName);
      await loadConnectionsAndSharedItems(); // Reload data after unsharing
      toast.success('Group unshared successfully');
    } catch (error) {
      console.error('Error unsharing group:', error);
      toast.error(error.message || 'Failed to unshare group');
    }
  };

  useEffect(() => {
    loadConnectionsAndSharedItems();
  }, [currentUser]);

  if (loading) {
    return <div className="text-center py-4">Loading connections...</div>;
  }

  if (error) {
    return <div className="text-red-600 py-4">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-6 h-6 text-[#04b53a]" />
        <h2 className="text-xl font-semibold">Your Connections</h2>
      </div>

      {connections.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-lg shadow">
          <p className="text-gray-500">
            No connections yet. Send invitations to connect with others!
          </p>
        </div>
      ) : (
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {connections.map((connection) => {
            return (
              <div 
                key={connection.id} 
                className="bg-white rounded-lg shadow p-6 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">{connection.otherUser.displayName}</h3>
                    <p className="text-sm text-gray-500">{connection.otherUser.email}</p>
                  </div>
                  <button
                    onClick={() => onShare(connection.otherUser.id)}
                    className="p-2 text-gray-600 hover:text-[#04b53a] transition-colors rounded-full hover:bg-gray-100"
                    title="Share cards or groups"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-2">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Shared Items:</span>
                    <div className="ml-2">
                      {sharedGroups[connection.otherUser.id]?.length > 0 && (
                        <div>
                          <button 
                            onClick={() => toggleGroups(connection.otherUser.id)}
                            className="flex items-center gap-2 font-medium text-gray-500"
                          >
                            <ChevronRight 
                              className={`w-4 h-4 transition-transform ${
                                expandedGroups[connection.otherUser.id] ? 'rotate-90' : ''
                              }`}
                            />
                            Groups ({sharedGroups[connection.otherUser.id].length})
                          </button>
                          {expandedGroups[connection.otherUser.id] && (
                            <div className="ml-6 mt-2 space-y-2">
                              {sharedGroups[connection.otherUser.id].map(group => (
                                <div key={group.id} className="flex items-center justify-between">
                                  <p className="text-gray-600">{group.groupName}</p>
                                  <button
                                    onClick={() => handleUnshareGroup(group, connection.otherUser.id)}
                                    className="text-red-500 hover:text-red-700"
                                    title="Unshare group"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {sharedCards[connection.otherUser.id]?.length > 0 && (
                        <div className="mt-2">
                          <button 
                            onClick={() => toggleCards(connection.otherUser.id)}
                            className="flex items-center gap-2 font-medium text-gray-500"
                          >
                            <ChevronRight 
                              className={`w-4 h-4 transition-transform ${
                                expandedCards[connection.otherUser.id] ? 'rotate-90' : ''
                              }`}
                            />
                            Cards ({sharedCards[connection.otherUser.id].length})
                          </button>
                          {expandedCards[connection.otherUser.id] && (
                            <div className="ml-6 mt-2 space-y-2">
                              {sharedCards[connection.otherUser.id].map(card => (
                                <div key={card.id} className="flex items-center justify-between">
                                  <p className="text-gray-600">
                                    {card.cardData?.firstName 
                                      ? `${card.cardData.firstName} ${card.cardData.lastName || ''}`
                                      : card.firstName 
                                        ? `${card.firstName} ${card.lastName || ''}`
                                        : 'Unnamed Card'}
                                  </p>
                                  <button
                                    onClick={() => handleUnshareCard(card, connection.otherUser.id)}
                                    className="text-red-500 hover:text-red-700"
                                    title="Unshare card"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {(!sharedGroups[connection.otherUser.id]?.length && 
                        !sharedCards[connection.otherUser.id]?.length) && (
                        <p className="text-gray-400">No items shared yet</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConnectionsList; 